// Probability calibration for the battle sim (pure — params injected).
//
// The raw Monte-Carlo win% is badly over-confident (when the sim says 95% the
// real rate is ~62%) and weakly discriminative. We fit a Platt scaling
// p_cal = sigmoid(a·logit(p) + b) on historical outcomes (tools/backtest.mjs
// --fit → src/data/simCalibration.json) that shrinks the probabilities toward
// their true rate — a big Brier-score win without touching the engine. A small
// `champEdge` adds the reigning champion's incumbency advantage in the Champ
// match (champions defend ~58%).
//
// This does NOT change which side is favored in the non-champ case (the argmax
// is preserved) — only how confident the number is. Engine realism is the lever
// for favorite-pick accuracy.
//
// The bundled params live in src/data/simCalibration.json; simCalibrationLive.js
// binds them for the app. Keeping this file params-injected (no JSON import) lets
// it run under plain Node for the tests.

const EPS = 1e-6;
const clamp01 = (p) => Math.min(1 - EPS, Math.max(EPS, p));
const logit = (p) => Math.log(clamp01(p) / (1 - clamp01(p)));
const sigmoid = (z) => 1 / (1 + Math.exp(-z));

// Calibrated P(team A wins) from the raw sim P(A wins). `params` = {a, b, champEdge}.
// `opts.champSide` is 'A' | 'B' | null — set it on the Champ match to the side that
// is the reigning Champion team so the incumbency edge lands on the right side.
export function calibrate(params, rawP, opts = {}) {
  const a = params && Number.isFinite(params.a) ? params.a : 1;
  const b = params && Number.isFinite(params.b) ? params.b : 0;
  const E = params && Number.isFinite(params.champEdge) ? params.champEdge : 0;
  let z = a * logit(rawP) + b;
  if (opts.champSide === 'A') z += E;
  else if (opts.champSide === 'B') z -= E;
  return sigmoid(z);
}

// Calibrated whole-percent win chances for both sides of a runBattles() result.
// Complementary (a + b = 100) — draws are folded into the binary P(A wins).
export function calibratedOdds(params, res, opts = {}) {
  const a = Math.round(calibrate(params, res.aWinPct, opts) * 100);
  return { a, b: 100 - a };
}
